import { useEffect, useRef, useState } from "react";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { Input } from "@/shared/components/ui/input";
import styles from "./SettingsPanel.module.css";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { ABBREVIATIONS } from "@/shared/constants/abbreviations";
import { useSettings } from "@/shared/contexts/SettingsContext";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

type Settings = {
  calendar: boolean;
  internal: boolean;
  sent_emails: boolean;
  minimum_units: boolean;
  track_after_exported: boolean;
  insurance_defense_mode: boolean;
  check_for_red_words: boolean;
  block_bill: boolean;
  paralegal_mode: boolean;
  abbreviations_enabled: boolean;
  word_count: number;
  time_to_track_emails: number;
  time_to_track_docs: number;
  prof_type: string;
  practice_type: string;
};

const defaultSettings: Settings = {
  calendar: false,
  internal: false,
  sent_emails: false,
  minimum_units: false,
  track_after_exported: true,
  insurance_defense_mode: false,
  check_for_red_words: false,
  block_bill: true,
  paralegal_mode: false,
  abbreviations_enabled: true,
  word_count: 30,
  time_to_track_emails: 20,
  time_to_track_docs: 30,
  prof_type: "",
  practice_type: "",
};

export default function SettingsPanel() {
  const { makeApiRequest } = useApi();
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const debounceRef = useRef<number | null>(null);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { refreshSettings: refreshContextSettings } = useSettings();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await makeApiRequest({ endpoint: "/settings", method: "GET" });
        const s = response?.data?.[0];
        if (s) {
          setSettings({
            calendar: !!s.calendar,
            internal: !!s.internal,
            sent_emails: !!s.sent_emails,
            minimum_units: !!s.minimum_units,
            track_after_exported: !!s.track_after_exported,
            insurance_defense_mode: !!s.insurance_defense_mode,
            check_for_red_words: !!s.check_for_red_words,
            block_bill: !!s.block_bill,
            paralegal_mode: !!s.paralegal_mode,
            abbreviations_enabled: s.abbreviations_enabled ?? true,
            word_count: s.word_count ?? 30,
            time_to_track_emails: s.time_to_track_emails ?? 20,
            time_to_track_docs: s.time_to_track_docs ?? 30,
            prof_type: s.prof_type ?? "",
            practice_type: s.practice_type ?? "",
          });
        }
      } catch (e) {
        if (e instanceof Error) {
          logger.captureException(e, { action: 'loadSettings' });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [makeApiRequest]);

  const saveSettings = async (next: Settings) => {
    try {
      await makeApiRequest({ endpoint: "/settings", method: "POST", body: next });
      await refreshContextSettings();
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'saveSettings' });
      }
    }
  };

  const update = (patch: Partial<Settings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => saveSettings(next), 100);
  };

  if (loading) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyText}>Loading settings…</div>
      </div>
    );
  }

  return (
    <div className={styles.settingsWrapper}>
      <div className={styles.settingsContainer}>
        {/* Left Column - Time Tracking & Billing */}
        <div className={styles.settingsLeftColumn}>
          {/* Time Tracking Section */}
          <div className={styles.settingsSection}>
            <h2 className={styles.settingsSectionHeader}>Time Tracking</h2>
            <div className={styles.settingsSectionContent}>
              <div className={styles.settingsItem} style={{ opacity: 0.5 }}>
                <div className={styles.settingsItemLabel}>Track calendar</div>
                <label className={styles.switch}>
                  <input type="checkbox" id="calendar" checked={settings.calendar} onChange={(e) => update({ calendar: e.target.checked })} disabled />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Track internal emails</div>
                <label className={styles.switch}>
                  <input type="checkbox" id="internal" checked={settings.internal} onChange={(e) => update({ internal: e.target.checked })} />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Track emails sent via phone</div>
                <label className={styles.switch}>
                  <input type="checkbox" id="sent_emails" checked={settings.sent_emails} onChange={(e) => update({ sent_emails: e.target.checked })} />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Re-track exported entries</div>
                <label className={styles.switch}>
                  <input type="checkbox" id="track_after_exported" checked={settings.track_after_exported} onChange={(e) => update({ track_after_exported: e.target.checked })} />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Minimum time (emails) - seconds</div>
                <Input
                  type="number"
                  id="time_to_track_emails"
                  value={settings.time_to_track_emails || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") return;
                    const numValue = parseInt(value) || 0;
                    update({ time_to_track_emails: Math.max(0, numValue) });
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === "" || parseInt(value) < 0) {
                      update({ time_to_track_emails: 0 });
                    }
                  }}
                  className="w-[100px]"
                />
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Minimum time (docs) - seconds</div>
                <Input
                  type="number"
                  id="time_to_track_docs"
                  value={settings.time_to_track_docs || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") return;
                    const numValue = parseInt(value) || 0;
                    update({ time_to_track_docs: Math.max(0, numValue) });
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === "" || parseInt(value) < 0) {
                      update({ time_to_track_docs: 0 });
                    }
                  }}
                  className="w-[100px]"
                />
              </div>
            </div>
          </div>

          {/* Billing Section */}
          <div className={styles.settingsSection}>
            <h2 className={styles.settingsSectionHeader}>Billing</h2>
            <div className={styles.settingsSectionContent}>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Minimum billable increments</div>
                <select
                  id="minimum_units"
                  value={settings.minimum_units ? "0.2" : "0.1"}
                  onChange={(e) => update({ minimum_units: e.target.value === "0.2" })}
                  className={styles.settingsSelect}
                >
                  <option value="0.1">0.1 hours</option>
                  <option value="0.2">0.2 hours</option>
                </select>
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Practice area</div>
                <Input id="practice_type" value={settings.practice_type} onChange={(e) => update({ practice_type: e.target.value })} className="w-[220px]" />
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Role</div>
                <Input id="prof_type" value={settings.prof_type} onChange={(e) => update({ prof_type: e.target.value })} className="w-[220px]" />
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Paralegal Mode</div>
                <label className={styles.switch}>
                  <input type="checkbox" id="paralegal_mode" checked={settings.paralegal_mode} onChange={(e) => update({ paralegal_mode: e.target.checked })} />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>Draft detailed narratives</div>
                <label className={styles.switch}>
                  <input type="checkbox" id="insurance_defense_mode" checked={settings.insurance_defense_mode} onChange={(e) => update({ insurance_defense_mode: e.target.checked })} />
                  <span className={styles.slider}></span>
                </label>
              </div>
              {!settings.insurance_defense_mode && (
                <>
                  <div className={styles.settingsItem}>
                    <div className={styles.settingsItemLabel}>Check for red words</div>
                    <label className={styles.switch}>
                      <input type="checkbox" id="check_for_red_words" checked={settings.check_for_red_words} onChange={(e) => update({ check_for_red_words: e.target.checked })} />
                      <span className={styles.slider}></span>
                    </label>
                  </div>
                  <div className={styles.settingsItemWithDescription}>
                    <div className={styles.settingsItemRow}>
                      <div className={styles.settingsItemLabel}>Show task breakdown in combined entries</div>
                      <label className={styles.switch}>
                        <input type="checkbox" id="block_bill" checked={settings.block_bill} onChange={(e) => update({ block_bill: e.target.checked })} />
                        <span className={styles.slider}></span>
                      </label>
                    </div>
                    <div className={styles.settingsDescription}>
                      {settings.block_bill
                        ? "When combining entries, each task's time and description will be shown separately in the narrative."
                        : "All tasks are combined into a single cohesive narrative paragraph without time breakdowns."}
                    </div>
                  </div>
                  <div className={styles.settingsItem}>
                    <div className={styles.settingsItemLabel}>Max word count</div>
                    <Input
                      type="number"
                      id="word_count"
                      value={settings.word_count || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") return;
                        const numValue = parseInt(value) || 0;
                        update({ word_count: Math.max(0, numValue) });
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === "" || parseInt(value) < 0) {
                          update({ word_count: 0 });
                        }
                      }}
                      className="w-[100px]"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

        </div>

        {/* Right Column - Abbreviations & Appearance */}
        <div className={styles.settingsRightColumn}>
          {/* Abbreviations Section */}
          <div className={`${styles.settingsSection} ${styles.settingsSectionGrow}`}>
            <h2 className={styles.settingsSectionHeader}>Abbreviations</h2>
            <div className={styles.settingsSectionContent}>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabelWithTooltip}>
                  <span>Enable abbreviations</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={styles.tooltipButton}
                          aria-label="Abbreviation help"
                        >
                          <HelpCircle className={styles.tooltipIcon} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="text-xs py-1 px-2 max-w-[250px]">
                        <p>When enabled, typing an abbreviation followed by a space will automatically expand it to the full text.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    id="abbreviations_enabled"
                    checked={settings.abbreviations_enabled}
                    onChange={(e) => update({ abbreviations_enabled: e.target.checked })}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <div
                className={styles.abbreviationsContainer}
                style={{ opacity: settings.abbreviations_enabled ? 1 : 0.4 }}
              >
                <div className={styles.abbreviationsGrid}>
                  {Object.entries(ABBREVIATIONS).map(([abbreviation, fullText]) => (
                    <div key={abbreviation} className={styles.abbreviationItem}>
                      <span className={styles.abbreviationShortcut}>{abbreviation}</span>
                      <span className={styles.abbreviationArrow}>→</span>
                      <span className={styles.abbreviationFullText}>{fullText}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Appearance Section */}
          <div className={styles.settingsSection}>
            <h2 className={styles.settingsSectionHeader}>Appearance</h2>
            <div className={styles.settingsSectionContent}>
              <div className={styles.settingsItem}>
                <div className={styles.settingsItemLabel}>{darkMode ? "Dark mode" : "Light mode"}</div>
                <label className={styles.switch}>
                  <input type="checkbox" id="dark_mode" checked={darkMode} onChange={() => toggleDarkMode()} />
                  <span className={styles.slider}></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
